import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { ErrorService } from './error.service';
import { finalize, map } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SpinnerService } from './spinner.service';
import { ParcelModalComponent } from '../components/parcel-modal/parcel-modal.component';
import { Observable } from 'rxjs';

@Injectable()
export class TrackParcelService {

	constructor(private _http: HttpClient,
				private _errorService: ErrorService,
				private _sanitizer: DomSanitizer,
				private _dialog: MatDialog,
				private _spinnerService: SpinnerService) {
	}

	getAndBuildParcelDetails(parcel) {
		return new Promise((resolve, reject) => {
			this.getDetails(parcel.id).subscribe((response) => {
				const parcelDetails = response;
				parcelDetails.formattedSignature = this._sanitizer.bypassSecurityTrustUrl('data:image/svg+xml;base64,' + parcelDetails.signature);
				resolve(parcelDetails);
			}, (error) => {
				reject();
				this._errorService.error(error, 'getDetails Failed', 'modal');
			});
		});
	}

	/**
	 * @api {get} /v2/Parcels/Track/Details Get Parcel Details
	 * @apiName trackParcels
	 * @apiParam {number} [id] ID
	 * @apiGroup Parcels
	 * @apiVersion 22.0.2
	 */
	getDetails(id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Parcels/Track/Details/${id}`);
	}

	/**
	 * @api {post} /v2/Parcels/Track/Summaries Track Parcels
	 * @apiName GetParcelSummariesV2
	 * @apiParam {string} [beginDate] Begin Date
	 * @apiParam {string} [endDate] End Date
	 * @apiParam {boolean} [includePartialMatch] Include Partial Match
	 * @apiParam {number} [numRecentParcels] Number of Recent Parcels
	 * @apiParam {number} [pageNum] Page Number
	 * @apiParam {number} [pageSize] Page Size
	 * @apiParam {string} [sortColumn] Sort Column
	 * @apiParam {string} [sortDirection] Sort Direction
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	trackParcels(searchCriteria, includePartialMatch, beginDate, endDate, numRecentParcels, pageNum, pageSize, sortColumn, sortDirection) {
		let params: HttpParams = new HttpParams();
		params = params.append('includePartialMatch', includePartialMatch);
		if (beginDate) {
			params = params.append('beginDate', beginDate);
		}
		if (endDate) {
			params = params.append('endDate', endDate);
		}
		if (numRecentParcels) {
			params = params.append('numRecentParcels', numRecentParcels);
		}
		if (pageNum) {
			params = params.append('pageNum', pageNum);
		}
		if (pageSize) {
			params = params.append('pageSize', pageSize);
		}
		if (sortColumn) {
			params = params.append('sortColumn', sortColumn);
		}
		if (sortDirection) {
			params = params.append('sortDirection', sortDirection);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/Parcels/Track/Summaries`, searchCriteria, { params });
	}

	/**
	 * @api {post} /v2/Parcels/Track/Summaries/CSV Track Parcels CSV
	 * @apiName GetParcelSummariesCsvV2
	 * @apiParam {string} [searchCriteria] Search Criteria
	 * @apiParam {boolean} [includePartialMatch] Include Partial Match
	 * @apiParam {string} [beginDate] Begin Date
	 * @apiParam {string} [endDate] End Date
	 * @apiParam {number} [numRecentParcels] Number of Recent Parcels
	 * @apiParam {string} [sortColumn] Sort Column
	 * @apiParam {string} [sortDirection] Sort Direction
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	trackParcelsCSV(searchCriteria, includePartialMatch, beginDate, endDate, numRecentParcels, sortColumn, sortDirection) {
		let params: HttpParams = new HttpParams();
		params = params.append('includePartialMatch', includePartialMatch);
		if (beginDate) {
			params = params.append('beginDate', beginDate);
		}
		if (endDate) {
			params = params.append('endDate', endDate);
		}
		if (numRecentParcels) {
			params = params.append('numRecentParcels', numRecentParcels);
		}
		if (sortColumn) {
			params = params.append('sortColumn', sortColumn);
		}
		if (sortDirection) {
			params = params.append('sortDirection', sortDirection);
		}
		return this._http.post(`${environment.apiUrl}/v2/Parcels/Track/Summaries/CSV`, searchCriteria, { params, responseType: 'text', observe: 'response' });
	}

	/**
	 * @api {get} /v2/Parcels/{parcelId}/PDF Returns a Stream of PDF generated parcel details
	 * @apiName GenerateParcelDetailsPdf
	 * @apiParam {string} [parcelId] Parcel ID
	 * @apiParam {string} [siteId] Site ID
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	generateParcelDetails(id) {
		let params: HttpParams = new HttpParams();
		if (environment.siteId !== null) {
			params = params.append('siteId', environment.siteId);
		}
		return this._http.get<Blob>(`${environment.apiUrl}/v2/Parcels/${id}/PDF`, { responseType: 'blob' as 'json', params: params });
	}


	/**
	 * @api {post} /v2/Parcels/Track/Summaries/PDF Returns a Stream of PDF generated parcel records
	 * @apiName GenerateParcelResultsPdf
	 * @apiParam {string} [searchCriteria] Search Criteria
	 * @apiParam {boolean} [includePartialMatch] Include Partial Match
	 * @apiParam {string} [beginDate] Begin Date
	 * @apiParam {string} [endDate] End Date
	 * @apiParam {number} [numRecentParcels] Number of Recent Parcels
	 * @apiParam {string} [sortColumn] Sort Column
	 * @apiParam {string} [sortDirection] Sort Direction
	 * @apiParam {string} [customerName] Customer Name
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	generateParcels(searchCriteria, includePartialMatch, beginDate, endDate, numRecentParcels, sortColumn, sortDirection, customerName) {
		let params: HttpParams = new HttpParams();

		params = params.append('includePartialMatch', includePartialMatch);
		if (beginDate) {
			params = params.append('beginDate', beginDate);
		}
		if (endDate) {
			params = params.append('endDate', endDate);
		}
		if (numRecentParcels) {
			params = params.append('numRecentParcels', numRecentParcels);
		}
		if (sortColumn) {
			params = params.append('sortColumn', sortColumn);
		}
		if (sortDirection) {
			params = params.append('sortDirection', sortDirection);
		}
		if (customerName) {
			params = params.append('customerName', customerName);
		}
		return this._http.post<Blob>(`${environment.apiUrl}/v2/Parcels/Track/Summaries/PDF`, searchCriteria, { params, responseType: 'blob' as 'json' })
	}

	/**
	 * @api {get} /v2/Parcels/{parcelId}/PDF Returns a Stream of PDF generated parcel details
	 * @apiName GenerateParcelDetailsPdf
	 * @apiParam {string} [parcelId] Parcel ID
	 * @apiParam {string} [siteId] Site ID
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	generateRouteStopParcelDetails(routeStopId : string) {
		let params: HttpParams = new HttpParams();
		if (environment.siteId !== null) {
			params = params.append('siteId', environment.siteId);
		}
		return this._http.get<Blob>(`${environment.apiUrl}/v2/RouteStop/${routeStopId}/Parcels/PDF`, { responseType: 'blob' as 'json', params: params });
	}

	/**
	 * @api {post} /v2/Parcels/{parcelId}/Email Email parcel details
	 * @apiName EmailParcelDetailsV2
	 * @apiParam {string} [parcelId] Parcel ID
	 * @apiParam {InetEmail} [email] Email info
	 * @apiParam {string} [siteId] Site ID
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	emailParcelDetails(id, email) {
		let params: HttpParams = new HttpParams();
		if (environment.siteId !== null) {
			params = params.append('siteId', environment.siteId);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/Parcels/${id}/Email`, email, { params })
			.pipe(map(res => res));
	}

	/**
	 * @api {post} /v2/RouteStop/${routeStopId}/Parcels/Email?siteId={siteId}
	 * @apiName EmailParcelsForRouteStopV2
	 * @apiParam {string} [routeStopId] Route Stop ID
	 * @apiParam {InetEmail} [email] Email info
	 * @apiParam {string} [siteId] Site ID
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	emailParcelsForRouteStop(routeStopId: string, email: any) {
		let params: HttpParams = new HttpParams();
		if (environment.siteId !== null) {
			params = params.append('siteId', environment.siteId);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/RouteStop/${routeStopId}/Parcels/Email`, email, { params })
			.pipe(map(res => res));
	}

	/**
	 * @api {post} /v2/Parcels/{parcelId}/Email Email parcel records
	 * @apiName EmailParcelResultsV2
	 * @apiParam {string} [searchRequestEmail] Search Criteria and Email info
	 * @apiParam {boolean} [includePartialMatch] Include Partial Match
	 * @apiParam {string} [beginDate] Begin Date
	 * @apiParam {string} [endDate] End Date
	 * @apiParam {number} [numRecentParcels] Number of Recent Parcels
	 * @apiParam {string} [sortColumn] Sort Column
	 * @apiParam {string} [sortDirection] Sort Direction
	 * @apiParam {string} [customerName] Customer Name
	 * @apiGroup Parcels
	 * @apiVersion 23.0.0
	 */
	emailParcels(searchRequestEmail, includePartialMatch, beginDate, endDate, numRecentParcels, sortColumn, sortDirection, customerName) {
		let params: HttpParams = new HttpParams();

		params = params.append('includePartialMatch', includePartialMatch);
		if (beginDate) {
			params = params.append('beginDate', beginDate);
		}
		if (endDate) {
			params = params.append('endDate', endDate);
		}
		if (numRecentParcels) {
			params = params.append('numRecentParcels', numRecentParcels);
		}
		if (sortColumn) {
			params = params.append('sortColumn', sortColumn);
		}
		if (sortDirection) {
			params = params.append('sortDirection', sortDirection);
		}
		if (customerName) {
			params = params.append('customerName', customerName);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/Parcels/Track/Summaries/Email`, searchRequestEmail, { params })
			.pipe(map(res => res));
	}

	/**
	* @api {post} /v3/RouteStops/{stopId}/Parcels
	* @apiParam {string} stopId
	*/
	addParcelByStopId(stopId: string, parcel: object) {
		return this._http.post<any>(`${environment.apiUrl}/v3/RouteStops/${stopId}/Parcels`, parcel);
	}

	/**
	* @api {post} /v3/Orders/{orderId}/Parcels
	* @apiParam {string} orderId
	*/
	addParcelByOrderId(orderId: string, parcel: object) {
		return this._http.post<any>(`${environment.apiUrl}/v3/Orders/${orderId}/Parcels`, parcel);
	}

	/**
	 * @api {put} v3/Parcels/{parcelId}/Inet
	 * @apiParam {string} parcelId
	 */
	editParcel(parcelId: string, parcel: object) {
		return this._http.put<any>(`${environment.apiUrl}/v3/Parcels/${parcelId}/Inet`, parcel);
	}

	/**
	 * @api {get} v2/Parcels/{parcelId}
	 * @apiParam {string} parcelId
	 */
	getParcel(parcelId: string, includeAssignments: boolean = false, includeAssignmentItems: boolean = false) {
		let params: HttpParams = new HttpParams();
		if (includeAssignments) {
			params = params.append('includeAssignments', includeAssignments);
		}
		if (includeAssignmentItems) {
			params = params.append('includeAssignmentItems', includeAssignmentItems);
		}
		return this._http.get<any>(`${environment.apiUrl}/v2/Parcels/${parcelId}`, { params });
	}

	editParcelModal(parcel, custId?) {
		return new Promise<any>((resolve) => {
			this._spinnerService.startSpinner();
			const openParcelDialog = (data, custId, parcel, resolve) => {
				const dialogConfig = new MatDialogConfig();
				let isMobile = window.matchMedia("only screen and (max-width: 480px)").matches;
				if (isMobile) {
					dialogConfig.height = 'calc(98vh - 70px)';
				}
				dialogConfig.autoFocus = false;
				dialogConfig.data = {
					parcel: data,
					custId: custId
				};

				const dialogRef = this._dialog.open(ParcelModalComponent, dialogConfig);
				dialogRef.afterClosed().subscribe((result: any) => {
					if (result?.length > 0) {
						const request = Object.assign(result[0], {
							parentId: data.parentId,
							parentType: data.parentType,
							rowVersion: data.rowVersion,
						});
						this._spinnerService.startSpinner();
						this.editParcel(parcel.id, request).subscribe({
							next: () => {
								this._spinnerService.stopSpinner();
								resolve({ parcelId: parcel.id, data: request });
							},
							error: (error) => {
								this._spinnerService.stopSpinner();
								this._errorService.error(error, 'editParcel failed', 'modal');
							}
						});
					}
				});
			}

			this.getParcel(parcel.id)
				.pipe(finalize(() => this._spinnerService.stopSpinner()))
				.subscribe((data) => {
					this._spinnerService.stopSpinner();
					if (custId || parcel.custId) {
						openParcelDialog(data, custId, parcel, resolve);
						return;
					}

					const isRouteStop = data.parentType == "ROUTE_STOP";
					const getDetails$ = isRouteStop
						? this._http.get(`${environment.apiUrl}/v2/RouteStops/${data.parentId}`)
						: this._http.get(`${environment.apiUrl}/v3/Orders/${data.parentId}/false`);

					this._spinnerService.startSpinner();
					getDetails$.subscribe({
						next: (details: any) => {
							this._spinnerService.stopSpinner();
							const resolvedCustId = isRouteStop ? details?.custId : details?.onDemandOrder?.custId;
							openParcelDialog(data, resolvedCustId, parcel, resolve);
						},
						error: (error) => {
							this._spinnerService.stopSpinner();
							this._errorService.error(error, `Failed to get ${data.parentType} details`, 'modal');
						}
					});
				});
		});
	}

	addParcelModal(id, custId, type: 'stop' | 'order') {
		return new Promise<any>((resolve) => {
			const dialogConfig = new MatDialogConfig();
			let isMobile = window.matchMedia("only screen and (max-width: 480px)").matches;
			if (isMobile) {
				dialogConfig.height = 'calc(98vh - 70px)';
			}
			dialogConfig.autoFocus = false;
			const order = {
				parcels: []
			}
			dialogConfig.data = {
				order,
				custId
			};
			const dialogRef = this._dialog.open(ParcelModalComponent, dialogConfig);
			dialogRef.afterClosed().subscribe((result: any) => {
				if (result) {
					let parcels = [];
					if (result == 'runCalculate') {
						parcels = order.parcels
					} else {
						parcels = result;
					}
					if (parcels.length > 0) {
						this._spinnerService.startSpinner();
						let addParcel$: Observable<any>;
						if (type == 'order') {
							addParcel$ = this.addParcelByOrderId(id, parcels)
						} else {
							addParcel$ = this.addParcelByStopId(id, parcels)
						}
						addParcel$.subscribe({
							next: () => {
								this._spinnerService.stopSpinner();
								resolve({ stopId: id, data: parcels });
							}, error: (error) => {
								this._spinnerService.stopSpinner();
								this._errorService.error(error, 'addParcel failed', 'modal');
							}
						});
					}
				}
			});
		});
	}
}
